import { Box, ButtonBase, Typography } from "@mui/material";
import type { PlayerProfile, TimeControl } from "@chessla/shared";
import type { ChatSelectionContext } from "./ChatContextPicker";

type AccountSnapshot = Pick<PlayerProfile, "username" | "ratings" | "record">;
const FORMATS: readonly TimeControl[] = ["rapid", "blitz", "bullet", "daily"];
const STARTERS = [
  ["OPENING TENDENCY", "What is my most played opening by format? State the definition and sample size."],
  ["RECENT FORMAT", "Review my recent results in the selected format. What decision deserves attention first?"],
  [
    "REPEATED WEAKNESS",
    "What verified decision problem repeats most in my games, and what is one cue for my next game?",
  ],
] as const;

export const ChatWorkspaceIntro = ({
  snapshot,
  context,
  onPrompt,
}: {
  snapshot: AccountSnapshot | null;
  context: ChatSelectionContext | null;
  onPrompt: (prompt: string) => void;
}) => {
  const starters = context?.gameId
    ? [
        ...STARTERS,
        [
          "SELECTED GAME",
          "Find the most teachable turning point in this selected game. Let me think before you reveal the verified line.",
        ] as const,
      ]
    : STARTERS;
  return (
    <Box component="section" sx={{ maxWidth: 960, mx: "auto", py: { xs: 4, sm: 7 }, minWidth: 0 }}>
      <Typography sx={{ color: "secondary.main", fontSize: ".64rem", fontWeight: 900, letterSpacing: ".14em" }}>
        NIKOLA · YOUR COACHING DESK
      </Typography>
      <Typography
        component="h1"
        sx={{
          mt: 1.5,
          maxWidth: 920,
          fontSize: { xs: "2.65rem", sm: "5.4rem" },
          lineHeight: 0.86,
          letterSpacing: "-.07em",
          fontWeight: 900,
        }}
      >
        ASK WHAT YOUR GAMES ARE TRYING TO TEACH YOU.
      </Typography>
      <Typography
        sx={{ mt: 2.5, maxWidth: 650, color: "text.secondary", fontSize: { xs: ".9rem", sm: "1rem" }, lineHeight: 1.7 }}
      >
        Nikola connects verified games, positions, and same-format Patterns. Choose context only when you want to narrow
        the evidence.
      </Typography>
      {snapshot && (
        <Box
          component="section"
          aria-label="Account snapshot"
          sx={{ mt: { xs: 4, sm: 5 }, p: { xs: 2, sm: 2.5 }, bgcolor: "primary.main", color: "background.default" }}
        >
          <Typography sx={{ fontSize: ".58rem", fontWeight: 900, letterSpacing: ".12em" }}>
            {snapshot.username.toUpperCase()} · AVAILABLE ACCOUNT FACTS
          </Typography>
          <Box
            sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,1fr)", sm: "repeat(4,1fr)" }, gap: 1, mt: 1.5 }}
          >
            {FORMATS.map((format) => {
              const rating = snapshot.ratings[format]?.current;
              const record = snapshot.record[format];
              return (
                <Box key={format} sx={{ borderTop: "2px solid", pt: 1 }}>
                  <Typography sx={{ fontSize: ".58rem", fontWeight: 900 }}>{format.toUpperCase()}</Typography>
                  <Typography sx={{ mt: 0.5, fontSize: "1.3rem", lineHeight: 1, fontWeight: 900 }}>
                    {rating ?? "—"}
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: ".58rem", opacity: 0.7 }}>
                    {record ? `${record.wins}W · ${record.draws}D · ${record.losses}L` : "NO IMPORTED RECORD"}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      <Typography sx={{ mt: { xs: 4, sm: 5 }, mb: 1.25, fontSize: ".62rem", fontWeight: 900, letterSpacing: ".12em" }}>
        START WITH A REAL QUESTION
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)" },
          borderTop: "1px solid",
          borderLeft: "1px solid",
          borderColor: "divider",
        }}
      >
        {starters.map(([label, prompt], index) => (
          <ButtonBase
            key={label}
            onClick={() => onPrompt(prompt)}
            sx={{
              minHeight: { xs: 106, sm: 126 },
              alignItems: "stretch",
              justifyContent: "stretch",
              p: 2,
              borderRight: "1px solid",
              borderBottom: "1px solid",
              borderColor: "divider",
              textAlign: "left",
              "&:hover,&:focus-visible": { bgcolor: "rgba(240,236,226,.06)" },
            }}
          >
            <Box>
              <Typography sx={{ color: "secondary.main", fontSize: ".58rem", fontWeight: 900, letterSpacing: ".1em" }}>
                0{index + 1} · {label}
              </Typography>
              <Typography sx={{ mt: 1, fontSize: { xs: ".82rem", sm: ".9rem" }, lineHeight: 1.5 }}>{prompt}</Typography>
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
};
